import React, { useMemo, useState, useEffect } from "react";
import {
  getDashboardStats,
  DashboardStats,
} from "../../services/api/admin/dashboard";
import { subscribeToOrders, OrderEventData } from "../../services/api/realtime";
import {
  Wallet,
  Ticket,
  Users,
  ArrowUpRight,
  TrendingUp,
  ShieldCheck,
  DollarSign,
  Activity,
  LogIn,
  ChevronRight,
  AlertCircle,
  Loader2,
  Bell,
} from "lucide-react";
import Swal from "sweetalert2";

interface DashboardHomeProps {
  onNavigate: (view: string) => void;
}

type ChartMetric = "revenue" | "volume";

export const DashboardHome: React.FC<DashboardHomeProps> = ({ onNavigate }) => {
  const [timeRange, setTimeRange] = useState<"today" | "7d" | "30d" | "1y">(
    "7d",
  );
  const [activeMetric, setActiveMetric] = useState<ChartMetric>("revenue");
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [newOrderCount, setNewOrderCount] = useState(0);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const result = await getDashboardStats();
      setStats(result);
      setNewOrderCount(0);
    } catch (error) {
      console.error("Failed to fetch dashboard stats:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeToOrders(
      (data: OrderEventData) => {
        setNewOrderCount((prev) => prev + 1);

        const Toast = Swal.mixin({
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 5000,
          timerProgressBar: true,
          didOpen: (toast) => {
            toast.onmouseenter = Swal.stopTimer;
            toast.onmouseleave = Swal.resumeTimer;
          },
        });
        Toast.fire({
          icon: "success",
          title: `Order Baru: ${data.bookingCode}`,
          text: `${data.customerName} - ${data.participant} pax`,
        });

        fetchData();
      },
      (data: OrderEventData) => {
        fetchData();
      },
    );

    return () => {
      unsubscribe();
    };
  }, []);

  const { chartData, labels, maxVal } = useMemo(() => {
    if (!stats) return { chartData: [], labels: [], maxVal: 0 };

    const chart = Array.isArray(stats.chart) ? stats.chart : [];

    const labels = chart.map((c) => {
      const d = new Date(c.date);
      return d.toLocaleDateString("id-ID", { day: "numeric", month: "short" });
    });

    const data = chart.map((c) =>
      activeMetric === "revenue" ? c.revenue : c.volume,
    );
    const rawMax = Math.max(...data, activeMetric === "revenue" ? 100000 : 10);

    return { chartData: data, labels, maxVal: rawMax * 1.25 };
  }, [stats, activeMetric]);

  if (isLoading && !stats)
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-gray-400">
        <Loader2 className="w-10 h-10 animate-spin mb-4" />
        <p className="font-bold font-mono uppercase tracking-[0.2em]">
          Synchronizing Data...
        </p>
      </div>
    );

  if (!stats)
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-gray-400">
        <AlertCircle className="w-10 h-10 mb-4" />
        <p className="font-bold font-mono uppercase tracking-[0.2em]">
          Gagal memuat data dashboard.
        </p>
      </div>
    );

  return (
    <div className="space-y-6 animate-fadeIn pb-12">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-black text-gray-900 tracking-tight uppercase">
            Control Center
          </h2>
          <p className="text-gray-500 text-sm font-medium">
            Monitoring Gate & Analisis Bisnis Laba.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex bg-white p-1 rounded-2xl shadow-sm border border-gray-200">
            {["today", "7d", "30d", "1y"].map((id) => (
              <button
                key={id}
                onClick={() => setTimeRange(id as any)}
                className={`px-4 py-2 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all ${
                  timeRange === id
                    ? "bg-giz-primary text-white shadow-md"
                    : "text-gray-500 hover:bg-gray-50"
                }`}
              >
                {id === "today"
                  ? "H-INI"
                  : id === "7d"
                    ? "7H"
                    : id === "30d"
                      ? "30H"
                      : "1TH"}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div
          onClick={() => setActiveMetric("revenue")}
          className={`p-7 rounded-[2.5rem] border transition-all cursor-pointer group relative overflow-hidden ${
            activeMetric === "revenue"
              ? "bg-indigo-600 text-white shadow-2xl shadow-indigo-200 border-indigo-500"
              : "bg-white text-gray-900 border-gray-100 hover:border-indigo-200 shadow-sm"
          }`}
        >
          <p
            className={`text-[11px] font-black uppercase tracking-[0.2em] mb-1 ${
              activeMetric === "revenue"
                ? "text-indigo-100 opacity-80"
                : "text-gray-400"
            }`}
          >
            Profit Bersih (Net)
          </p>
          <h3 className="text-3xl font-black tracking-tight">
            Rp {stats.overall.totalNetIncome.toLocaleString("id-ID")}
          </h3>
          <div
            className={`mt-3 flex items-center gap-1 text-[10px] font-black uppercase ${
              activeMetric === "revenue" ? "text-white" : "text-emerald-600"
            }`}
          >
            <TrendingUp className="w-3 h-3" /> Lihat Grafik Laba
          </div>
          <Wallet
            className={`absolute -bottom-4 -right-4 w-24 h-24 opacity-10 transition-transform group-hover:scale-110 ${
              activeMetric === "revenue" ? "text-white" : "text-indigo-600"
            }`}
          />
        </div>

        <div
          onClick={() => setActiveMetric("volume")}
          className={`p-7 rounded-[2.5rem] border transition-all cursor-pointer group relative overflow-hidden ${
            activeMetric === "volume"
              ? "bg-indigo-600 text-white shadow-2xl shadow-indigo-200 border-indigo-500"
              : "bg-white text-gray-900 border-gray-100 hover:border-indigo-200 shadow-sm"
          }`}
        >
          <p
            className={`text-[11px] font-black uppercase tracking-[0.2em] mb-1 ${
              activeMetric === "volume"
                ? "text-indigo-100 opacity-80"
                : "text-gray-400"
            }`}
          >
            Total Pemesanan
          </p>
          <h3 className="text-3xl font-black tracking-tight">
            {stats.overall.allOrders}{" "}
            <span className="text-sm font-bold opacity-40 uppercase tracking-widest">
              Orders
            </span>
          </h3>
          <div
            className={`mt-3 flex items-center gap-1 text-[10px] font-black uppercase ${
              activeMetric === "volume" ? "text-white" : "text-indigo-600"
            }`}
          >
            <Users className="w-3 h-3" /> Lihat Grafik Volume
          </div>
          <Ticket
            className={`absolute -bottom-4 -right-4 w-24 h-24 opacity-10 transition-transform group-hover:scale-110 ${
              activeMetric === "volume" ? "text-white" : "text-indigo-600"
            }`}
          />
        </div>

        <div
          className="bg-white p-7 rounded-[2.5rem] border border-gray-100 shadow-sm flex flex-col justify-between cursor-pointer hover:border-indigo-200 transition-all"
          onClick={() => onNavigate("orders")}
        >
          <div className="flex justify-between items-start">
            <div className="space-y-1">
              <p className="text-gray-400 text-[11px] font-black uppercase tracking-[0.2em] mb-1">
                Total Booking
              </p>
              <h3 className="text-3xl font-black text-gray-900 tracking-tighter">
                {stats.overall.allOrders}
                <span className="text-sm font-bold text-gray-400 ml-2">
                  booking
                </span>
              </h3>
            </div>
            <div className="bg-indigo-50 p-3 rounded-2xl">
              <Activity className="w-5 h-5 text-giz-primary" />
            </div>
          </div>
          <div className="mt-4 flex gap-2 flex-wrap">
            <span className="bg-amber-100 text-amber-700 px-2 py-1 rounded-full text-[10px] font-bold">
              {stats.overall.ordersByStatus?.pending || 0} Pending
            </span>
            <span className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full text-[10px] font-bold">
              {stats.overall.ordersByStatus?.paid || 0} Paid
            </span>
            <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-[10px] font-bold">
              {stats.overall.ordersByStatus?.completed || 0} Done
            </span>
          </div>
        </div>

        <div
          className="bg-emerald-600 text-white p-7 rounded-[2.5rem] shadow-2xl relative overflow-hidden group hover:scale-[1.02] transition-transform cursor-pointer"
          onClick={() => onNavigate("checkin")}
        >
          <div className="relative z-10">
            <p className="text-emerald-100 text-[11px] font-black uppercase tracking-[0.2em] mb-1 opacity-80">
              Sudah Check-In
            </p>
            <h3 className="text-3xl font-black tracking-tighter">
              {stats.today.checkedIn}{" "}
              <span className="text-sm font-normal opacity-50 uppercase tracking-widest">
                Pax
              </span>
            </h3>
            <p className="text-[10px] font-black text-white/60 mt-3 bg-white/10 w-fit px-3 py-1 rounded-full uppercase tracking-widest">
              Buka Gate
            </p>
          </div>
          <LogIn className="absolute -bottom-4 -right-4 w-24 h-24 opacity-10 transition-transform" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          <div className="bg-white rounded-[3rem] border border-gray-200 p-8 md:p-10 flex flex-col shadow-sm">
            <div className="flex justify-between items-center mb-10">
              <h3 className="font-black text-gray-900 text-lg uppercase tracking-widest flex items-center gap-3">
                {activeMetric === "revenue" ? (
                  <DollarSign className="w-6 h-6 text-indigo-500" />
                ) : (
                  <Users className="w-6 h-6 text-indigo-500" />
                )}
                Grafik{" "}
                {activeMetric === "revenue"
                  ? "Laba Bersih"
                  : "Volume Pengunjung"}{" "}
                (7 Hari Terakhir)
              </h3>
            </div>

            <div className="overflow-x-auto pb-4 scrollbar-thin">
              <div
                className="h-80 flex items-end gap-3 px-2 pt-12 relative"
                style={{ minWidth: labels.length * 50 + "px" }}
              >
                <div className="absolute inset-0 flex flex-col justify-between pointer-events-none opacity-[0.03] pt-12">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div
                      key={i}
                      className="border-t-2 border-black w-full"
                    ></div>
                  ))}
                </div>
                {chartData.map((val, idx) => (
                  <div
                    key={idx}
                    className="flex-1 flex flex-col items-center group h-full justify-end min-w-[35px]"
                  >
                    {val > 0 && (
                      <div
                        style={{ height: `${(val / maxVal) * 100}%` }}
                        className="w-full rounded-t-2xl bg-slate-100 group-hover:bg-indigo-300 transition-all duration-700 ease-out relative"
                      >
                        <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-slate-900 text-white text-[9px] px-2 py-1 rounded shadow-xl opacity-0 group-hover:opacity-100 transition-opacity">
                          {activeMetric === "revenue"
                            ? `Rp ${val.toLocaleString()}`
                            : `${val} Pax`}
                        </div>
                      </div>
                    )}
                    {val === 0 && (
                      <div className="h-1.5 w-full bg-gray-50 rounded-t-full"></div>
                    )}
                    <span className="text-[9px] mt-4 font-black uppercase text-gray-400 whitespace-nowrap">
                      {labels[idx]}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-8">
          <div className="bg-white rounded-[2.5rem] border border-gray-200 p-8 shadow-sm">
            <h3 className="font-black text-gray-800 mb-8 text-xs uppercase tracking-[0.25em] flex items-center gap-3">
              <TrendingUp className="w-5 h-5 text-orange-400" /> Top Affiliates
            </h3>
            <div className="space-y-5">
              {stats.topAffiliates.map((aff, i) => (
                <div
                  key={i}
                  className="flex justify-between items-center p-4 rounded-3xl bg-gray-50 border border-transparent hover:border-indigo-200 transition-all cursor-default"
                >
                  <div>
                    <span className="font-black text-gray-800 text-sm block truncate max-w-[120px] tracking-tight uppercase">
                      {aff.name}
                    </span>
                    <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1 block">
                      {aff.code}
                    </span>
                  </div>
                  <div className="text-right">
                    <span className="font-black text-indigo-600 text-xs font-mono">
                      Rp {aff.sales.toLocaleString()}
                    </span>
                    <span className="block text-[9px] font-bold text-gray-400 uppercase mt-0.5">
                      Sales
                    </span>
                  </div>
                </div>
              ))}
              {stats.topAffiliates.length === 0 && (
                <div className="text-center py-10 border-2 border-dashed border-gray-100 rounded-3xl">
                  <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">
                    No Affiliate Data
                  </p>
                </div>
              )}
            </div>
            <button
              onClick={() => onNavigate("affiliate")}
              className="w-full mt-8 py-4 bg-gray-900 text-white text-[11px] font-black uppercase tracking-[0.2em] rounded-2xl hover:bg-black transition-all shadow-xl"
            >
              Manage Partners
            </button>
          </div>

          <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-[2.5rem] p-8 shadow-2xl relative overflow-hidden">
            <div className="relative z-10">
              <h3 className="font-black text-sm mb-4 flex items-center gap-3 uppercase tracking-widest">
                <ShieldCheck className="w-5 h-5 text-emerald-400" /> Business
                Sync
              </h3>
              <p className="text-xs text-slate-400 leading-relaxed font-bold uppercase tracking-tight">
                Data sinkron secara real-time. Dashboard ini hanya menampilkan
                transaksi riil dari pengunjung GIZ & RIW.
              </p>
            </div>
            <ArrowUpRight className="absolute -top-6 -right-6 w-24 h-24 opacity-5" />
          </div>
        </div>
      </div>
    </div>
  );
};
